// Copyright 2026, Imprivata, Inc.  All rights reserved.

// NOTE: This module currently uses POST /feature-flags/find only. To also consume
// POST /global-feature-flags/find (useful e.g. for a feature-flag admin UI that
// enumerates all evaluated flags), fire it in parallel from fetchServerFeatureFlags
// and merge its map underneath the batch results.

import type { QueryClient } from '@tanstack/react-query';

export const FEATURE_FLAGS_QUERY_KEY = 'feature-flags';
import { FEATURE_FLAG_REGISTRY, type FeatureFlagType, type FlagValue, type ServerFlagEntry } from './registry';
import { Endpoints, StorageKeys, SystemType } from '@api/constants';
import { fetcher } from '@api/utils';

type FeatureFlagFindItem = {
  hasValue: boolean;
  value?: string; //missing **ONLY** if hasValue is false
};

let serverFlagCache = new Map<string, FlagValue>();

// Exported only for test isolation — not part of the public API.
export function resetServerFlagCacheForTest(): void {
  serverFlagCache = new Map();
}

export function parseServerFlagValue(value: string, type: FeatureFlagType): FlagValue {
  switch (type) {
    case 'boolean': return value === 'true';
    case 'integer': return Number.parseInt(value, 10);
    case 'double':  return Number.parseFloat(value);
    case 'string':  return value;
  }
}

async function fetchServerFeatureFlags(
  tenantId: string | undefined,
  systemType: SystemType,
): Promise<Map<string, FlagValue>> {
  const serverEntries = Object.values(FEATURE_FLAG_REGISTRY).filter(
    (e) => !('legacy' in e),
  ) as ServerFlagEntry[];

  if (serverEntries.length === 0) {
    return new Map();
  }

  const items = serverEntries.map(({ featureFlagKey, featureFlagType }) => ({
    featureFlagKey,
    featureFlagType,
  }));
  const registryByKey = new Map(serverEntries.map(e => [e.featureFlagKey, e]));

  try {
    const response = (await fetcher(
      Endpoints.FEATURE_FLAGS_FIND,
      {
        method: 'POST',
        body: JSON.stringify({ items }),
        systemType,
        workflowId:
          systemType === SystemType.SETUP ? sessionStorage.getItem(StorageKeys.WORKFLOW_ID) : null,
      },
      tenantId,
    )) as Record<string, FeatureFlagFindItem>;

    const result = new Map<string, FlagValue>();
    for (const [key, entry] of Object.entries(response)) {
      const registryEntry = registryByKey.get(key);
      if (entry.value !== undefined && registryEntry) {
        result.set(key, parseServerFlagValue(entry.value, registryEntry.featureFlagType));
      }
    }
    serverFlagCache = result;
  } catch {
    serverFlagCache = new Map();
    console.warn('[featureFlagsServerCache] failed to fetch server feature flags');
  }
  return serverFlagCache;
}

export function readServerFlag(
  featureFlagKey: string,
  _type: FeatureFlagType,
  defaultValue: FlagValue,
): FlagValue {
  if (serverFlagCache.size === 0 && import.meta.env.DEV) {
    console.warn(`[featureFlagsServerCache] flag "${featureFlagKey}" read before cache was primed`);
  }
  return serverFlagCache.has(featureFlagKey)
    ? (serverFlagCache.get(featureFlagKey) as FlagValue)
    : defaultValue;
}

export function primeServerFeatureFlags(
  queryClient: QueryClient,
  tenantId: string | undefined,
  systemType: SystemType = SystemType.PORTAL,
): Promise<Map<string, FlagValue>> {
  return queryClient.fetchQuery({
    queryKey: [FEATURE_FLAGS_QUERY_KEY, tenantId],
    queryFn: () => fetchServerFeatureFlags(tenantId, systemType),
  });
}
